import {
  Button,
  CloseSVG,
  EditHollowSVG,
  UndoSVG,
  Icon,
} from "@newsela/angelou";
import PropTypes from "prop-types";

import {
  actionPrefixes,
  objectTypes,
  actionNames,
  withTrackEvent,
} from "@newsela/snitch";
import { getIsSavedAssignment } from "@/assignments/utils/assignmentUrlHelpers";
import { pollsProptype } from "@/view/PollContainer/PollProptypes";
import getEventPropertiesContentType from "@/view/utils/getEventPropertiesContentType";
import useFocusManager from "@/view/utils/useFocusManager";
import usePollContext from "@/view/utils/usePollContext";
import PollResponseCount from "../Poll/PollResponseCount";
import Poll from "../Poll";
import {
  cancelEditsButtonCss,
  cancelEditsIconCss,
  editButtonCss,
  editIconCss,
  editingIconCss,
  editingTextCss,
  footerCss,
} from "./TeacherPollContainer.style";
import PollsDropdown from "./PollsDropdown";

const propTypes = {
  contentType: PropTypes.string,
  currentContentLevelId: PropTypes.string,
  isStudentAssignmentEmbedActive: PropTypes.bool,
  isLiteUser: PropTypes.bool,
  isPremiumContent: PropTypes.bool,
  levelId: PropTypes.string,
  openAssignmentEditor: PropTypes.func,
  polls: pollsProptype,
  selectedAssignmentId: PropTypes.string,
};

const ButtonWithTrackEvent = withTrackEvent(Button);

const TeacherPollContainer = ({
  contentType,
  currentContentLevelId,
  isStudentAssignmentEmbedActive,
  isLiteUser,
  isPremiumContent,
  levelId,
  openAssignmentEditor,
  polls,
  selectedAssignmentId,
}) => {
  const { focusManagerRefs } = useFocusManager();
  const isSavedAssignment = getIsSavedAssignment(selectedAssignmentId);
  const assignmentEditorHeadingRef =
    focusManagerRefs?.current?.assignmentEditorHeading;
  const {
    activePoll,
    isEditMode,
    isPollSelected,
    handleAssignmentPoll,
    setIsEditMode,
    setIsPollSelected,
    isValidateFormFieldsActive,
    setIsValidateFormFieldsActive,
  } = usePollContext();

  if (!activePoll) return null;

  const {
    can_see_results: canSeeResults,
    data: pollData,
    id: activePollId,
    is_template: isTemplate,
  } = activePoll;

  const resultsFooter = (
    <div css={footerCss(canSeeResults)}>
      <PollResponseCount pollData={pollData} />
    </div>
  );

  if (canSeeResults && isPollSelected) {
    return (
      <Poll eyebrow="new!" footer={resultsFooter} title="See poll results" />
    );
  }

  const footerEventProperties = {
    objectType: objectTypes.assignment,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: getEventPropertiesContentType(contentType),
      content_id: levelId,
      description: "Open assignment editor",
    },
  };

  const editButtonEventProperties = {
    objectType: objectTypes.poll,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: getEventPropertiesContentType(contentType),
      content_id: levelId,
      description: "Edit poll",
    },
  };

  const cancelEditsButtonEventProperties = {
    objectType: objectTypes.poll,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: getEventPropertiesContentType(contentType),
      content_id: levelId,
      description: "Choose another poll",
    },
  };

  const closeButtonEventProperties = {
    objectType: objectTypes.poll,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: getEventPropertiesContentType(contentType),
      content_id: levelId,
      description: "Close poll",
    },
  };

  const editButton = (
    <ButtonWithTrackEvent
      __cssFor={{
        root: editButtonCss,
        Icon: {
          root: editIconCss,
        },
      }}
      dataProps={{ "data-qa-selector": "edit-poll" }}
      flavor={Button.flavors.secondary}
      icon={{
        SvgComponent: EditHollowSVG,
        alignment: Button.iconAlignments.left,
      }}
      onClick={() => {
        setIsEditMode(true);
      }}
      {...editButtonEventProperties}
    >
      Edit poll
    </ButtonWithTrackEvent>
  );

  const cancelEditsButton = (
    <ButtonWithTrackEvent
      __cssFor={{ root: cancelEditsButtonCss }}
      dataProps={{ "data-qa-selector": "choose-another-poll" }}
      flavor={Button.flavors.polarTertiary}
      onClick={() => {
        setIsEditMode(false);
      }}
      {...cancelEditsButtonEventProperties}
    >
      <Icon
        SvgComponent={UndoSVG}
        __cssFor={{ root: cancelEditsIconCss }}
        size="SM"
      />
      Cancel
    </ButtonWithTrackEvent>
  );

  const editingYourPollText = (
    <div css={editingTextCss}>
      <Icon
        SvgComponent={EditHollowSVG}
        __cssFor={{ root: editingIconCss }}
        size="SM"
      />
      Editing your poll
    </div>
  );

  const handleSaveOrAssignButtonClick = () => {
    if (isEditMode) {
      setIsValidateFormFieldsActive(true);
      handleAssignmentPoll({ assignmentId: selectedAssignmentId });
      return;
    }

    if (isSavedAssignment && isTemplate) {
      // Adding a new poll from template to a previously saved assignment that had no poll.
      handleAssignmentPoll({ assignmentId: selectedAssignmentId });
      return;
    }

    // Move focus to the editor when its not a saved assignment and not editing
    // to prompt users to create an assignment in order to assign a poll.
    if (!isSavedAssignment) {
      openAssignmentEditor();
      assignmentEditorHeadingRef?.focus({ preventScroll: true });
      assignmentEditorHeadingRef?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const shouldRenderSaveButton =
    isEditMode || (isSavedAssignment && isTemplate);

  const footer = () => {
    return !shouldRenderSaveButton ? null : (
      <div css={footerCss(canSeeResults)}>
        <ButtonWithTrackEvent
          disabled={isLiteUser && isPremiumContent}
          dataProps={{ "data-qa-selector": "add-to-assignment-poll" }}
          flavor={Button.flavors.primary}
          onClick={handleSaveOrAssignButtonClick}
          {...footerEventProperties}
        >
          Save
        </ButtonWithTrackEvent>
      </div>
    );
  };

  const pollsDropdown = (
    <PollsDropdown
      activePollId={activePollId}
      currentContentLevelId={currentContentLevelId}
      isSavedAssignment={isSavedAssignment}
      polls={polls}
    />
  );

  const closeButton = (
    <ButtonWithTrackEvent
      ariaProps={{ "aria-label": "Close poll" }}
      dataProps={{ "data-qa-selector": "close-poll" }}
      flavor={Button.flavors.polarTertiary}
      icon={{ SvgComponent: CloseSVG }}
      onClick={() => setIsPollSelected(false)}
      size={Button.sizes.md}
      {...closeButtonEventProperties}
    />
  );

  return isPollSelected ? (
    <Poll
      closeButton={closeButton}
      editButtonSlot={isEditMode ? cancelEditsButton : editButton}
      eyebrow="new!"
      footer={footer()}
      isValidateFormFieldsActive={isValidateFormFieldsActive}
      pollSelectorSlot={isEditMode ? editingYourPollText : pollsDropdown}
      shouldShowCloseButton={!isStudentAssignmentEmbedActive}
      title="Add student poll to assignment"
    />
  ) : null;
};

TeacherPollContainer.propTypes = propTypes;

export default TeacherPollContainer;
